// ─────────────────────────────────────────────────────────────────────────
// batch-39 onboarding (FO-337) — Reglementen & akkoorden.
//
// Gedeelde DTO's + Zod-schema's voor de beheerbare beleidsdocument-lijst
// (OnboardingReglement) en de samengevoegde wizard-stap. Het gedeelde
// schrijf-schema wordt door de API (`.safeParse`) én het backoffice-form
// (client-side) gebruikt (validatie-standaard); doelgroep hergebruikt het
// FO-149-patroon uit `uitrusting-doelgroep.ts` — geen nieuw mechanisme.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import {
  UitrustingDoelgroepInputSchema,
  type UitrustingArtikelDoelgroepDTO,
} from './uitrusting-doelgroep'

// ─── Beheer-DTO (Instellingen → Onboarding → Reglementen & akkoorden) ───────

export interface OnboardingReglementDTO {
  id: string
  titel: string
  omschrijving: string | null
  volgorde: number
  actief: boolean
  documentId: string | null
  /** Bestandsnaam + versienummer van de actuele (hoogste levende) documentversie. */
  bestandsnaam: string | null
  versieNr: number | null
  /** FO-149-doelgroep-blok (geen regels = iedereen). */
  doelgroep: UitrustingArtikelDoelgroepDTO
  /** Aantal medewerkers met een levende leesbevestiging op dit reglement. */
  aantalBevestigingen: number
  /** FO-126-vlag op het gekoppelde document (her-akkoord bij zittend personeel). */
  herAkkoordActief: boolean
  /** Versie-historie van het gekoppelde document (nieuwste eerst). */
  versies: OnboardingReglementVersieDTO[]
}

export interface OnboardingReglementVersieDTO {
  versionNr: number
  fileName: string
  uploadedAt: string
  /** Aantal levende leesbevestigingen dat exact deze versie bevestigde. */
  aantalBevestigingen: number
}

// ─── Wizard-DTO (stap "Reglementen & akkoorden" in de welkom-flow) ──────────

export interface OnboardingStapReglementDTO {
  id: string
  titel: string
  omschrijving: string | null
  /** Actuele versie waarvoor het akkoord geldt (nooit stilzwijgend wisselen). */
  versieNr: number
  bestandsnaam: string
  /** Eerder gegeven akkoord van deze medewerker (of null). */
  bevestigdOp: string | null
  bevestigdeVersieNr: number | null
  /** True = het eerdere akkoord telt nog (zelfde/legacy versie) — geen actie nodig. */
  bevestigdGeldig: boolean
  /** True = deze (of een nieuwere) versie is binnen deze onboarding geopend. */
  geopendOpVersie: boolean
  /** Voor lopende oude akkoord-stappen: het checkbox-veld in het stap-schema. */
  legacyVeldId: string | null
}

/** Blok in de stap-DTO: de reglementen + de openen-verplicht-instelling. */
export interface OnboardingStapReglementenBlokDTO {
  /** FO-337 §2.1 — vinkje pas aan te zetten nadat het document geopend is. */
  openenVerplicht: boolean
  items: OnboardingStapReglementDTO[]
}

// ─── Gedeelde schrijf-schema's (API + backoffice-form) ──────────────────────

/** Aanmaken/bewerken van een reglement (beheer). Upload/koppeling gaat apart. */
export const OnboardingReglementUpsertSchema = z.object({
  titel: z.string().trim().min(1, 'Titel is verplicht').max(200),
  omschrijving: z.string().trim().max(1000).nullable().optional(),
  volgorde: z.number().int().min(0).max(999).optional(),
  actief: z.boolean().optional(),
  /** Koppel een bestaand bibliotheek-document (i.p.v. een nieuwe upload). */
  documentId: z.string().min(1).nullable().optional(),
  /** FO-149-doelgroep (vervang-semantiek; leeg = iedereen; weggelaten = ongemoeid). */
  doelgroep: UitrustingDoelgroepInputSchema.optional(),
})

export type OnboardingReglementUpsertInput = z.infer<typeof OnboardingReglementUpsertSchema>

/**
 * Akkoorden-blok bij het indienen van de reglementen-stap: per reglement het
 * versienummer waarop de medewerker akkoord gaf. De server weigert een
 * verouderd versienummer (409 REGLEMENT_VERSIE_GEWIJZIGD) zodat niemand
 * stilzwijgend een andere versie tekent (FO-337 §5).
 */
export const ReglementAkkoordenSchema = z
  .array(
    z.object({
      reglementId: z.string().min(1),
      versieNr: z.number().int().min(1),
    }),
  )
  .max(100)

export type ReglementAkkoordenInput = z.infer<typeof ReglementAkkoordenSchema>

// ─── Instellingen-DTO ────────────────────────────────────────────────────────

/** Org-brede onboarding-instellingen (singleton; zie OnboardingInstellingen). */
export interface OnboardingInstellingenDTO {
  reglementOpenenVerplicht: boolean
  werktMetCaoSchalen: boolean
}

// ─── Constanten ──────────────────────────────────────────────────────────────

/** Bron-waarde op DocumentLeesbevestiging voor akkoorden uit de welkom-wizard. */
export const LEESBEVESTIGING_BRON_ONBOARDING = 'ONBOARDING'
